import { browserT } from '#/i18n/browser';
import { makeHeaders } from '#/actions/server/fetch';
import { ENV } from '#/data/constants';
import { REVISIONCONFIGTYPE } from '#/hooks';
import { SelectItemType } from 'types/common';

// Request Types
interface CorrectErrorDocument {
  content: string;
  language: string;
}

interface CorrectErrorConfig {
  disable_split_sentence?: boolean;
  disable_caret_spacing?: boolean;
  disable_vx_spacing?: boolean;
  enable_cleanup_whitespace?: boolean;
  enable_sentence_check?: boolean;
  treat_as_title?: boolean;
}

interface CorrectErrorRequest {
  document: CorrectErrorDocument;
  encoding_type: string;
  custom_dict_names?: string[];
  config?: CorrectErrorConfig;
}

// Response Types
interface RevisionItem {
  revised: string;
  category?: string;
  score?: number;
  helpId?: string;
  [key: string]: any;
}

interface OriginBlock {
  content: string;
  beginOffset: number;
  length: number;
}

interface RevisedBlock {
  origin: OriginBlock;
  revised: string;
  revisions: RevisionItem[];
  nested: any[];
  lemma: string;
  pos: string;
  [key: string]: any;
}

interface WhitespaceCleanupRange {
  offset: number;
  length: number;
  position?: string;
}

interface RevisedSentence {
  origin: string;
  revised: string;
  [key: string]: any;
}

interface ReviseHelp {
  id: string;
  category: string;
  comment: string;
  examples: string[];
  ruleArticle: string;
  [key: string]: any;
}

interface CorrectErrorResponse {
  origin: string;
  revised: string;
  revisedBlocks: RevisedBlock[];
  whitespaceCleanupRanges: WhitespaceCleanupRange[];
  revisedSentences: RevisedSentence[];
  helps: Record<string, ReviseHelp>;
  language?: string;
  tokensCount?: number;
  [key: string]: any;
}

export async function fetchCorrectError(
  text: string,
  apiKey: string,
  customDict: SelectItemType[],
  configType: REVISIONCONFIGTYPE
): Promise<{
  origin: string;
  revised: string;
  revisedSentencesList: RevisedSentence[];
  revisedBlocks: RevisedBlock[];
  helps: [string, ReviseHelp][];
  tokenCount: number;
  whitespaceCleanupRangesList: WhitespaceCleanupRange[];
} | null> {
  try {
    const url = `https://${ENV.GRPC_BAREUN_ADDRESS}/bareun.RevisionService/CorrectError`;

    const requestBody: CorrectErrorRequest = {
      document: {
        content: text,
        language: 'ko_KR'
      },
      encoding_type: 'UTF16',
      custom_dict_names: customDict?.map((e) => e.value) || [],
      config: {
        disable_split_sentence: !configType.disable_split_sentence,
        disable_caret_spacing: !configType.disable_caret_spacing,
        disable_vx_spacing: !configType.disable_vx_spacing,
        enable_cleanup_whitespace: configType.enable_cleanup_whitespace,
        enable_sentence_check: configType.enable_sentence_check,
        treat_as_title: false
      }
    };
    const headers = makeHeaders(apiKey, 'bareun-web');
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
      redirect: 'follow'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`
      );
    }

    const data: CorrectErrorResponse = await response.json();

    // Convert helps object to array format
    const helpsArray: [string, ReviseHelp][] = Object.entries(data.helps || {});

    return {
      origin: data.origin,
      revised: data.revised,
      revisedSentencesList: data.revisedSentences || [],
      revisedBlocks: data.revisedBlocks || [],
      helps: helpsArray,
      tokenCount: data.tokensCount || 0,
      whitespaceCleanupRangesList: data.whitespaceCleanupRanges || []
    };
  } catch (error) {
    console.error('fetchCorrectError error:', error);
    throw new Error(
      error instanceof Error
        ? error.message
        : browserT('server')('correctFetch.unknown')
    );
  }
}

// ─── Stream Response Types ───────────────────────────────

export interface StreamFirstCorrectError {
  origin: string;
  revised: string;
  revisedBlocks: RevisedBlock[];
  whitespaceCleanupRanges: WhitespaceCleanupRange[];
  revisedSentences: RevisedSentence[];
  helps: Record<string, ReviseHelp>;
  language?: string;
  tokensCount?: number;
  reqId?: string;
  thinkingIds?: number[];
}

export interface CancelledRevision {
  reqId: string;
  thinkingId: number;
}

export interface PostRevision {
  reqId: string;
  thinkingId: number;
  revision: RevisionItem;
  help?: ReviseHelp;
}

export interface ProgressRevision {
  reqId: string;
  thinkingId: number;
  thinkingMsg: string;
}

export type StreamCorrectErrorMessage =
  | { first: StreamFirstCorrectError }
  | { cancelled: CancelledRevision }
  | { post: PostRevision }
  | { progress: ProgressRevision };

// ─── gRPC-Web(+json) Streaming Frame Parser ──────────────

/**
 * gRPC-Web server-streaming 응답을 파싱하기 위한 버퍼드 리더.
 * 각 메시지는 5바이트 헤더(flags 1B + length 4B BE) + payload 로 구성.
 */
class ConnectStreamReader {
  private reader: ReadableStreamDefaultReader<Uint8Array>;
  private buffer = new Uint8Array(0);
  private done = false;

  constructor(reader: ReadableStreamDefaultReader<Uint8Array>) {
    this.reader = reader;
  }

  private async fill(needed: number): Promise<boolean> {
    while (this.buffer.byteLength < needed) {
      if (this.done) return false;
      const { done, value } = await this.reader.read();
      if (done) {
        this.done = true;
        return this.buffer.byteLength >= needed;
      }
      const merged = new Uint8Array(this.buffer.byteLength + value.byteLength);
      merged.set(this.buffer);
      merged.set(value, this.buffer.byteLength);
      this.buffer = merged;
    }
    return true;
  }

  async readExact(n: number): Promise<Uint8Array | null> {
    if (!(await this.fill(n))) return null;
    const result = this.buffer.slice(0, n);
    this.buffer = this.buffer.slice(n);
    return result;
  }
}

// 한글 작업창(#301)의 서버 쪽 스트림 중계도 같은 프레임 파서·인코더를 쓴다.
export async function* parseConnectStream(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<StreamCorrectErrorMessage> {
  const reader = new ConnectStreamReader(body.getReader());
  const decoder = new TextDecoder();

  while (true) {
    const header = await reader.readExact(5);
    if (!header) break;

    const flags = header[0];
    const length =
      (header[1] << 24) | (header[2] << 16) | (header[3] << 8) | header[4];

    const payload = await reader.readExact(length);
    if (!payload) break;

    // gRPC-Web trailers frame (MSB=1)
    if (flags & 0x80) {
      const trailerText = decoder.decode(payload);
      const grpcStatusMatch = trailerText.match(/grpc-status:\s*(\d+)/i);
      const grpcMessageMatch = trailerText.match(/grpc-message:\s*([^\r\n]+)/i);

      const grpcStatus = grpcStatusMatch ? Number(grpcStatusMatch[1]) : 0;
      if (grpcStatus !== 0) {
        const grpcMessage = grpcMessageMatch
          ? decodeURIComponent(grpcMessageMatch[1])
          : 'grpc stream error';
        throw new Error(`gRPC status ${grpcStatus}: ${grpcMessage}`);
      }
      break;
    }

    const json = JSON.parse(decoder.decode(payload));
    yield json as StreamCorrectErrorMessage;
  }
}

export function encodeGrpcWebJsonFrame(obj: unknown): Uint8Array {
  const payload = new TextEncoder().encode(JSON.stringify(obj));
  const frame = new Uint8Array(5 + payload.byteLength);
  frame[0] = 0x00; // data frame
  frame[1] = (payload.byteLength >>> 24) & 0xff;
  frame[2] = (payload.byteLength >>> 16) & 0xff;
  frame[3] = (payload.byteLength >>> 8) & 0xff;
  frame[4] = payload.byteLength & 0xff;
  frame.set(payload, 5);
  return frame;
}

// ─── Server-Streaming: StreamCorrectError ────────────────

/**
 * StreamCorrectError RPC를 Connect protocol POST로 호출한다.
 * 전체 문서를 한 번에 전송하고, 스트리밍 응답(first → progress/post/cancelled)을 읽는다.
 */
export async function* fetchStreamCorrectError(
  text: string,
  apiKey: string,
  customDict: SelectItemType[],
  configType: REVISIONCONFIGTYPE,
  signal?: AbortSignal
): AsyncGenerator<StreamCorrectErrorMessage> {
  const url = `https://${ENV.GRPC_BAREUN_ADDRESS}/bareun.RevisionService/StreamCorrectError`;

  const requestBody = {
    document: { content: text, language: 'ko_KR' },
    encoding_type: 'UTF16',
    custom_dict_names: customDict?.map((e) => e.value) || [],
    config: {
      disable_split_sentence: !configType.disable_split_sentence,
      disable_caret_spacing: !configType.disable_caret_spacing,
      disable_vx_spacing: !configType.disable_vx_spacing,
      enable_cleanup_whitespace: configType.enable_cleanup_whitespace,
      enable_sentence_check: configType.enable_sentence_check,
      treat_as_title: false
    },
    req_id: '0'
  };

  console.log('fetchStreamCorrectError 요청 URL:', requestBody);

  const headers = makeHeaders(apiKey, 'bareun-web');
  // 서버가 gRPC-Web framing을 기대하는 경우(JSON도 프레이밍 필요)
  headers.set('Content-Type', 'application/grpc-web+json');
  headers.set('Accept', 'application/grpc-web+json');
  headers.set('X-Grpc-Web', '1');
  headers.set('X-User-Agent', 'grpc-web-javascript/0.1');

  const framedBody = encodeGrpcWebJsonFrame(requestBody);
  const framedBodyStable = Uint8Array.from(framedBody);
  const framedBodyBlob = new Blob([framedBodyStable], {
    type: 'application/grpc-web+json'
  });

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: framedBodyBlob,
    signal
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`
    );
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  yield* parseConnectStream(response.body);
}
